import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/affordable-life-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Budget Calculator Data ─────────────────────────────────────────────── */

type AgeRange = '25-29' | '30-34' | '35-39' | '40-44' | '45-49' | '50-54' | '55-59' | '60-64';
type PolicyType = 'term10' | 'term20' | 'term30' | 'whole';

interface BudgetResult {
  coverage: string;
  carrier: string;
  type: string;
  monthlyRate: number;
  annualSavings: number;
  amBest: string;
  badge?: string;
}

// Rate per $1,000 of coverage per month (Preferred Plus, non-tobacco)
const RATE_PER_1K: Record<AgeRange, Record<PolicyType, number>> = {
  '25-29': { term10: 0.040, term20: 0.054, term30: 0.072, whole: 1.72 },
  '30-34': { term10: 0.044, term20: 0.060, term30: 0.084, whole: 2.08 },
  '35-39': { term10: 0.052, term20: 0.076, term30: 0.108, whole: 2.64 },
  '40-44': { term10: 0.072, term20: 0.116, term30: 0.168, whole: 3.52 },
  '45-49': { term10: 0.104, term20: 0.176, term30: 0.272, whole: 4.72 },
  '50-54': { term10: 0.152, term20: 0.276, term30: 0.436, whole: 6.44 },
  '55-59': { term10: 0.236, term20: 0.456, term30: 0, whole: 9.40 },
  '60-64': { term10: 0.392, term20: 0.816, term30: 0, whole: 13.08 },
};

const POLICY_LABELS: Record<PolicyType, string> = {
  term10: '10-Year Term',
  term20: '20-Year Term',
  term30: '30-Year Term',
  whole: 'Whole Life',
};

const CARRIER_BY_TYPE: Record<PolicyType, string> = {
  term10: 'Banner Life',
  term20: 'Banner Life',
  term30: 'Protective Life',
  whole: 'North American Company',
};

const AMBEST_BY_TYPE: Record<PolicyType, string> = {
  term10: 'A+',
  term20: 'A+',
  term30: 'A+',
  whole: 'A+',
};

function calcCoverage(budget: number, ageRange: AgeRange, policyType: PolicyType): number {
  const rate = RATE_PER_1K[ageRange][policyType];
  if (rate === 0) return 0;
  const rawK = Math.floor(budget / rate);
  // Round down to nearest $50K
  return Math.floor(rawK / 50) * 50;
}

/* ─── FAQs ───────────────────────────────────────────────────────────────── */

const FAQS = [
  {
    q: 'What is the most affordable life insurance in Indianapolis?',
    a: 'The most affordable life insurance in Indianapolis is 10-year or 20-year term life insurance. A healthy 30-year-old Indianapolis resident can get $500,000 of 20-year term coverage for as little as $22–$30/month depending on the carrier and health class. For seniors or those seeking permanent coverage, final expense insurance starts at $30/month for $10,000–$25,000 of whole life coverage. The key to finding the most affordable option is comparing multiple carriers — rates for the same coverage can vary by 30–50% between companies.',
  },
  {
    q: 'How much does affordable life insurance cost per month in Indianapolis?',
    a: 'Affordable life insurance in Indianapolis starts at approximately $15–$20/month for a healthy 25–35-year-old seeking $250,000 of 10-year term coverage. For $500,000 of 20-year term coverage, a healthy 35-year-old pays $30–$38/month. A 45-year-old pays $88–$106/month for the same coverage. Whole life insurance costs significantly more — $250,000 of whole life coverage for a 35-year-old runs $335–$374/month. The most affordable option for most Indianapolis families is term life insurance during working years.',
  },
  {
    q: 'Can I get affordable life insurance in Indianapolis with no medical exam?',
    a: 'Yes. No-exam life insurance is available in Indianapolis for coverage up to $500,000. Transamerica and Mutual of Omaha offer simplified underwriting — just answer health questions online or by phone, no blood draw or doctor visit required. No-exam policies typically cost 10–20% more than fully underwritten policies, but the speed (24–48 hour approval) and convenience make them popular. For the most affordable no-exam option, a healthy 35-year-old Indianapolis resident can get $250,000 of 20-year term coverage for approximately $28–$35/month without a medical exam.',
  },
  {
    q: 'What is the cheapest life insurance for Indianapolis families?',
    a: 'For Indianapolis families, the cheapest life insurance is term life insurance purchased at a young age. A 30-year-old couple can each get $500,000 of 20-year term coverage for approximately $30/month each — $60/month total for $1,000,000 of combined family coverage. This covers the mortgage (average Indianapolis home: $285,000), income replacement, and future college costs. The cheapest approach: buy term life now while you\'re young and healthy, lock in your rate, and convert to permanent coverage later if needed.',
  },
  {
    q: 'How do I find affordable life insurance in Indianapolis with health conditions?',
    a: 'Finding affordable life insurance in Indianapolis with health conditions requires working with an independent agent who can shop multiple carriers. Different carriers rate health conditions differently — for example, Mutual of Omaha is known for competitive rates on controlled type 2 diabetes, while Transamerica specializes in applicants with high blood pressure. Conditions like controlled hypertension, well-managed diabetes, or a history of cancer don\'t automatically disqualify you — they just affect which carrier offers the best rate. An independent agent compares all options simultaneously to find the most affordable coverage for your specific health profile.',
  },
  {
    q: 'Is $30/month enough for life insurance in Indianapolis?',
    a: 'Yes — $30/month buys meaningful life insurance coverage in Indianapolis. At $30/month, a healthy 35-year-old Indianapolis resident can get approximately $500,000 of 20-year term coverage (Banner Life, Preferred Plus class). A 40-year-old can get approximately $250,000 of 20-year term coverage. A 50-year-old can get approximately $100,000 of 20-year term coverage. For seniors, $30/month buys $10,000–$15,000 of final expense whole life coverage with no medical exam. Use our Budget Calculator above to see exactly what $30/month (or any budget) buys at your age.',
  },
  {
    q: 'What is the difference between cheap and affordable life insurance in Indianapolis?',
    a: '"Cheap" life insurance often means low coverage amounts, weak carriers, or policies with hidden restrictions. "Affordable" life insurance means the right coverage from a financially strong carrier at the lowest available rate. At Hoosier Life Insurance, we focus on affordable — not cheap. We only work with A-rated carriers (AM Best A or better), which means your family is protected by financially strong companies. The difference between the cheapest and most expensive carrier for the same coverage can be 30–50%, which is why comparing multiple carriers is essential to finding truly affordable life insurance in Indianapolis.',
  },
  {
    q: 'How much life insurance can I afford on a $50/month budget in Indianapolis?',
    a: 'On a $50/month budget, an Indianapolis resident can get substantial life insurance coverage. A healthy 30-year-old can get approximately $750,000–$1,000,000 of 20-year term coverage for $50/month. A 40-year-old can get approximately $400,000–$500,000 of 20-year term coverage. A 50-year-old can get approximately $150,000–$200,000 of 20-year term coverage. Use our Budget Calculator above to see exactly what $50/month buys at your specific age and preferred policy type.',
  },
  {
    q: 'Does Indianapolis have lower life insurance rates than other cities?',
    a: 'Yes — Indianapolis and Indiana generally have life insurance rates 8–12% below the national average. This is partly because Indiana has lower mortality rates than many coastal states, and partly because the cost of living (which affects carrier pricing models) is lower. Indianapolis residents pay less for the same coverage than residents of Chicago, New York, or Los Angeles. Additionally, Indiana has a competitive insurance market with many carriers actively competing for business, which keeps rates low.',
  },
  {
    q: 'What is the most affordable life insurance for Indianapolis seniors?',
    a: 'For Indianapolis seniors (ages 55–80), the most affordable life insurance options are: (1) Final expense whole life insurance — $10,000–$25,000 of permanent coverage with no medical exam, starting at $30–$45/month for a 65-year-old. (2) Guaranteed issue whole life — available to anyone ages 50–85 regardless of health, $5,000–$25,000 of coverage, no health questions. (3) 10-year term life — if you\'re in good health, a 60-year-old can get $100,000 of 10-year term coverage for approximately $68–$81/month. Mutual of Omaha and Transamerica offer the most competitive senior rates in Indianapolis.',
  },
];

/* ─── Rate Table Data ────────────────────────────────────────────────────── */

const RATE_TABLE = [
  { age: '25', coverage: '$250K', term: '20-Year Term', monthly: '$17', carrier: 'Banner Life' },
  { age: '25', coverage: '$500K', term: '20-Year Term', monthly: '$27', carrier: 'Banner Life' },
  { age: '30', coverage: '$250K', term: '20-Year Term', monthly: '$19', carrier: 'Banner Life' },
  { age: '30', coverage: '$500K', term: '20-Year Term', monthly: '$30', carrier: 'Banner Life' },
  { age: '35', coverage: '$250K', term: '20-Year Term', monthly: '$22', carrier: 'Banner Life' },
  { age: '35', coverage: '$500K', term: '20-Year Term', monthly: '$38', carrier: 'Banner Life' },
  { age: '40', coverage: '$250K', term: '20-Year Term', monthly: '$32', carrier: 'Banner Life' },
  { age: '40', coverage: '$500K', term: '20-Year Term', monthly: '$58', carrier: 'Banner Life' },
  { age: '45', coverage: '$250K', term: '20-Year Term', monthly: '$48', carrier: 'Banner Life' },
  { age: '45', coverage: '$500K', term: '20-Year Term', monthly: '$88', carrier: 'Banner Life' },
  { age: '50', coverage: '$250K', term: '20-Year Term', monthly: '$72', carrier: 'Banner Life' },
  { age: '50', coverage: '$500K', term: '20-Year Term', monthly: '$138', carrier: 'Banner Life' },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */

const TESTIMONIALS = [
  {
    name: 'Darnell & Keisha W.',
    location: 'Lawrence, IN',
    quote: 'We thought life insurance was out of our budget. Benjamin found us $500K each for $58/month combined. We had no idea it was that affordable. Wish we\'d called sooner.',
    stars: 5,
    budget: '$58/mo total',
    coverage: '$1M combined',
  },
  {
    name: 'Sandra M.',
    location: 'Beech Grove, IN',
    quote: 'Single mom on a tight budget. Benjamin got me $250K of 20-year term for $22/month. That\'s less than my Netflix subscription. My kids are protected and I can actually afford it.',
    stars: 5,
    budget: '$22/mo',
    coverage: '$250K',
  },
  {
    name: 'Gary & Linda P.',
    location: 'Speedway, IN',
    quote: 'We\'re both in our 50s and thought it was too late for affordable coverage. Benjamin found us $200K each for $148/month combined. We finally have peace of mind.',
    stars: 5,
    budget: '$148/mo total',
    coverage: '$400K combined',
  },
  {
    name: 'Marcus T.',
    location: 'Southport, IN',
    quote: 'I had a budget of $40/month. Benjamin ran the numbers on 6 carriers and found me $500K of 30-year term for $36/month. Locked in at 32 — rate never changes.',
    stars: 5,
    budget: '$36/mo',
    coverage: '$500K · 30-Year',
  },
];

/* ─── Cost Myths ─────────────────────────────────────────────────────────── */

const MYTHS = [
  {
    myth: 'Life insurance costs $100+/month',
    reality: 'A healthy 35-year-old gets $500K of 20-year term for $38/month — less than most streaming subscriptions combined.',
    icon: 'ri-money-dollar-circle-line',
  },
  {
    myth: 'You need perfect health to get affordable rates',
    reality: 'Controlled conditions like high blood pressure or type 2 diabetes still qualify for Standard or Standard Plus rates — often $50–$80/month for $500K.',
    icon: 'ri-heart-pulse-line',
  },
  {
    myth: 'Older people can\'t get affordable coverage',
    reality: 'A healthy 55-year-old gets $250K of 10-year term for $62/month. Seniors can get $15K of final expense coverage for $45–$60/month.',
    icon: 'ri-user-heart-line',
  },
  {
    myth: 'You need a medical exam to get a good rate',
    reality: 'No-exam policies are available up to $500K. They cost 10–20% more, but many Indianapolis residents prefer the 24-hour approval.',
    icon: 'ri-hospital-line',
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

export default function AffordableLifeInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Budget Calculator state
  const [budget, setBudget] = useState<number>(40);
  const [ageRange, setAgeRange] = useState<AgeRange>('35-39');
  const [calcResults, setCalcResults] = useState<BudgetResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const title = 'Affordable Life Insurance Indianapolis IN | From $15/mo | Hoosier Life';
    const description = 'Find affordable life insurance in Indianapolis, IN starting at $15/month. Use our Budget Calculator to see exactly what your budget buys. Compare 20+ A-rated carriers. Local independent agent. Call (317) 648-2983.';
    const keywords = 'affordable life insurance Indianapolis, cheap life insurance Indianapolis, low cost life insurance Indianapolis Indiana, budget life insurance Indianapolis, affordable life insurance Indianapolis IN';

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/708f2c76963a4a65d90f7f7a5a0aab9c.jpg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
      { name: 'ICBM', content: '39.7684, -86.1581' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-15T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
        about: [
          { '@type': 'Thing', name: 'Affordable Life Insurance Indianapolis' },
          { '@type': 'Thing', name: 'Cheap Life Insurance Indianapolis' },
          { '@type': 'Thing', name: 'Budget Life Insurance Indianapolis Indiana' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Affordable Life Insurance Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Affordable Life Insurance',
        name: 'Affordable Life Insurance Indianapolis',
        description: 'Budget-focused life insurance for Indianapolis and Marion County residents. Compare 20+ A-rated carriers to find the most affordable coverage for your budget. Independent agent — no obligation.',
        url: CANONICAL,
        provider: {
          '@type': 'InsuranceAgency',
          name: 'Hoosier Life Insurance',
          telephone: '+1-317-648-2983',
          url: SITE_URL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5625 German Church Rd',
            addressLocality: 'Indianapolis',
            addressRegion: 'IN',
            postalCode: '46235',
            addressCountry: 'US',
          },
          geo: { '@type': 'GeoCoordinates', latitude: 39.7684, longitude: -86.1581 },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '13:00' },
          ],
        },
        areaServed: [
          { '@type': 'City', name: 'Indianapolis', containedIn: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Carmel' },
          { '@type': 'City', name: 'Fishers' },
          { '@type': 'City', name: 'Noblesville' },
          { '@type': 'State', name: 'Indiana' },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '127',
          bestRating: '5',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const handleCalculate = () => {
    const results: BudgetResult[] = (
      ['term10', 'term20', 'term30', 'whole'] as PolicyType[]
    )
      .map(pt => {
        const coverageK = calcCoverage(budget, ageRange, pt);
        if (coverageK === 0) return null;
        const monthlyRate = Math.round(RATE_PER_1K[ageRange][pt] * coverageK);
        const annualSavings = Math.round((budget - monthlyRate) * 12);
        return {
          coverage: coverageK >= 1000 ? `$${coverageK / 1000}M` : `$${coverageK.toLocaleString()}`,
          carrier: CARRIER_BY_TYPE[pt],
          type: POLICY_LABELS[pt],
          monthlyRate,
          annualSavings,
          amBest: AMBEST_BY_TYPE[pt],
          badge: pt === 'term20' ? 'Most Popular' : pt === 'term30' ? 'Best Value' : undefined,
        } as BudgetResult;
      })
      .filter(Boolean) as BudgetResult[];
    setCalcResults(results);
  };

  const budgetDisplay = `$${budget}/mo`;

  const ageRangeOptions: { value: AgeRange; label: string }[] = [
    { value: '25-29', label: 'Age 25–29' },
    { value: '30-34', label: 'Age 30–34' },
    { value: '35-39', label: 'Age 35–39' },
    { value: '40-44', label: 'Age 40–44' },
    { value: '45-49', label: 'Age 45–49' },
    { value: '50-54', label: 'Age 50–54' },
    { value: '55-59', label: 'Age 55–59' },
    { value: '60-64', label: 'Age 60–64' },
  ];

  const budgetPresets = [15, 25, 40, 60, 80, 100, 150, 200];

  // Memoized quick-look table for sidebar
  const quickLookRows = useMemo(() => RATE_TABLE.slice(0, 8), []);

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Life Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Affordable Coverage</span>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20cozy%20Indianapolis%20Indiana%20suburban%20home%20living%20room%20family%20sitting%20together%20on%20couch%20reviewing%20budget%20documents%20and%20laptop%20showing%20life%20insurance%20options%20golden%20hour%20lighting%20comfortable%20middle%20class%20home%20environment%20parents%20and%20children%20feeling%20secure%20and%20protected%20Marion%20County%20Indiana%20photorealistic%20warm%20tones&width=1440&height=680&seq=ali-indy-hero-2025a&orientation=landscape"
            alt="Affordable Life Insurance Indianapolis Indiana — Coverage Starting at $15/Month"
            title="Affordable Life Insurance in Indianapolis, Indiana — Budget Calculator"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={680}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/88 to-[#1A3A52]/35"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[700px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-money-dollar-circle-line text-sm"></i>
              Indianapolis &amp; Marion County · Budget-First Coverage
            </span>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Affordable Life Insurance in{' '}
              <span className="text-[#D97642]">Indianapolis, Indiana</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[620px]">
              Coverage starts at <strong className="text-white">$15/month</strong> for Indianapolis residents. Use our <strong className="text-white">Budget Calculator</strong> below to see exactly what your monthly budget buys — then compare 20+ A-rated carriers to find the lowest rate.
            </p>

            {/* Hero Budget Teaser */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { budget: '$20/mo', gets: '$250K · Age 30' },
                { budget: '$38/mo', gets: '$500K · Age 35' },
                { budget: '$58/mo', gets: '$500K · Age 40' },
                { budget: '$88/mo', gets: '$500K · Age 45' },
              ].map(item => (
                <div key={item.budget} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-lg font-extrabold text-[#D97642]">{item.budget}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.gets}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="#budget-calculator"
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calculator-line text-lg"></i>
                Use Budget Calculator
              </a>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '$15/mo', label: 'Lowest available rate for Indianapolis residents' },
              { value: '8–12%', label: 'Below national average — Indiana rates are lower' },
              { value: '20+', label: 'A-rated carriers compared to find your best price' },
              { value: '$0', label: 'Cost to compare — quotes are always free' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Affordable Life Insurance in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'A healthy 35-year-old Indianapolis resident gets $500,000 of 20-year term coverage for $38/month — less than most gym memberships',
                  'Indiana life insurance rates are 8–12% below the national average — Hoosiers pay less for the same coverage',
                  'Rates are locked in at purchase — a 30-year-old who buys today pays the same rate at age 50',
                  'No-exam life insurance is available up to $500,000 — approved in 24–48 hours, no doctor visit required',
                  'Comparing 20+ carriers can save $30–$50/month vs. going with the first quote you receive',
                  'Quotes are always free — Hoosier Life is compensated by the carrier, never by charging you a fee',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT + SIDEBAR ───────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT COLUMN */}
          <div>

            {/* ── BUDGET CALCULATOR ─────────────────────────────────────── */}
            <div id="budget-calculator" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Budget Calculator</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                What Does Your Budget Buy in Indianapolis?
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Enter your monthly budget and age range to see exactly how much life insurance coverage you can get — across all four policy types — from top A-rated carriers.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                {/* Controls */}
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-calculator-line text-[#D97642]"></i>
                    Your Budget &amp; Age
                  </h3>

                  {/* Budget Slider */}
                  <div className="mb-7">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-xs font-semibold text-white/70 uppercase tracking-wide">Monthly Budget</label>
                      <span className="text-2xl font-extrabold text-[#D97642]">{budgetDisplay}</span>
                    </div>
                    <input
                      type="range"
                      min={10}
                      max={300}
                      step={5}
                      value={budget}
                      onChange={e => { setBudget(Number(e.target.value)); setCalcResults(null); }}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer"
                      style={{ accentColor: '#D97642' }}
                    />
                    <div className="flex justify-between text-xs text-white/40 mt-1">
                      <span>$10/mo</span>
                      <span>$300/mo</span>
                    </div>
                    {/* Preset buttons */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {budgetPresets.map(p => (
                        <button
                          key={p}
                          onClick={() => { setBudget(p); setCalcResults(null); }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${budget === p ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          ${p}/mo
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Age Range */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age Range</label>
                    <div className="flex flex-wrap gap-2">
                      {ageRangeOptions.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setAgeRange(opt.value); setCalcResults(null); }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${ageRange === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleCalculate}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base"
                  >
                    <i className="ri-calculator-line text-lg"></i>
                    Calculate My Coverage Options
                  </button>
                </div>

                {/* Results */}
                {calcResults !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h3 className="text-base font-bold text-[#1A3A52]">
                          What {budgetDisplay} Buys — {ageRangeOptions.find(o => o.value === ageRange)?.label}
                        </h3>
                        <p className="text-xs text-gray-400 mt-0.5">Preferred Plus · Non-tobacco · Indianapolis, IN · Estimates only</p>
                      </div>
                    </div>

                    {calcResults.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <i className="ri-information-line text-3xl mb-2 block"></i>
                        <p className="text-sm">Budget too low for this age range. Try increasing your budget or selecting a younger age range.</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4">
                        {calcResults.map((result, idx) => (
                          <div
                            key={result.type}
                            className={`rounded-xl border p-5 ${result.badge === 'Most Popular' ? 'border-[#D97642]/40 bg-[#FFF4ED]' : result.badge === 'Best Value' ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-[#F9F7F4]'}`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="text-sm font-bold text-[#1A3A52]">{result.type}</span>
                                  {result.badge && (
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${result.badge === 'Most Popular' ? 'bg-[#D97642] text-white' : 'bg-[#4A7C59] text-white'}`}>
                                      {result.badge}
                                    </span>
                                  )}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {result.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-500 mb-3">{result.carrier} · Indianapolis, IN</p>
                                <div className="flex items-center gap-6">
                                  <div>
                                    <div className="text-xs text-gray-400 mb-0.5">Coverage Amount</div>
                                    <div className="text-2xl font-extrabold text-[#1A3A52]">{result.coverage}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-400 mb-0.5">Est. Monthly Rate</div>
                                    <div className="text-2xl font-extrabold text-[#D97642]">${result.monthlyRate}<span className="text-sm font-normal text-gray-400">/mo</span></div>
                                  </div>
                                  {result.annualSavings > 0 && (
                                    <div>
                                      <div className="text-xs text-gray-400 mb-0.5">Under Budget By</div>
                                      <div className="text-lg font-bold text-[#4A7C59]">${result.annualSavings}/yr</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 font-extrabold text-sm ${idx === 0 ? 'bg-[#D97642] text-white' : 'bg-white text-gray-400 border border-gray-200'}`}>
                                {idx + 1}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-5 p-4 bg-[#1A3A52]/5 rounded-xl">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Coverage amounts shown are estimates based on Preferred Plus health class (excellent health, non-tobacco). Actual coverage depends on your health class, carrier underwriting, and final approval. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized quote.
                      </p>
                    </div>

                    <button
                      onClick={() => setShowBookingModal(true)}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                    >
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact Quote — Free 15-Min Call
                    </button>
                  </div>
                )}

                {calcResults === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-calculator-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Set your budget and age range above, then click <strong className="text-[#1A3A52]">Calculate My Coverage Options</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Affordable Rate Table */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Rates</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Affordable Life Insurance Rates in Indianapolis — 2026
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                These are real rates available to Indianapolis residents in good health (Preferred Plus class, non-tobacco). Rates are locked in at purchase and never increase.
              </p>

              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#1A3A52] px-6 py-4">
                  <h3 className="text-sm font-bold text-white">20-Year Term Life Insurance Rates — Indianapolis, IN (Preferred Plus · Non-Tobacco)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-5 py-3 text-left font-semibold text-gray-600">Age</th>
                        <th className="px-5 py-3 text-left font-semibold text-gray-600">Coverage</th>
                        <th className="px-5 py-3 text-left font-semibold text-gray-600">Policy Type</th>
                        <th className="px-5 py-3 text-center font-semibold text-[#D97642]">Monthly Rate</th>
                        <th className="px-5 py-3 text-left font-semibold text-gray-600">Carrier</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {RATE_TABLE.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                          <td className="px-5 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                          <td className="px-5 py-3 text-gray-700">{row.coverage}</td>
                          <td className="px-5 py-3 text-gray-500">{row.term}</td>
                          <td className="px-5 py-3 text-center font-extrabold text-[#D97642] text-base">{row.monthly}</td>
                          <td className="px-5 py-3 text-gray-500">{row.carrier}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <p className="text-xs text-gray-400">Rates shown are estimates for Preferred Plus health class (excellent health, non-tobacco). Actual rates depend on full underwriting. Last updated April 2026.</p>
                </div>
              </div>
            </div>

            {/* Cost Myths Busted */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Common Misconceptions</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                4 Myths About Life Insurance Costs in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Most Indianapolis residents overestimate the cost of life insurance by 3–5x. Here's the truth:
              </p>
              <div className="grid sm:grid-cols-2 gap-5">
                {MYTHS.map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-red-50 flex-shrink-0">
                        <i className={`${item.icon} text-xl text-red-400`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold text-red-500 uppercase tracking-wide">Myth</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-700 mb-3 line-through opacity-60">"{item.myth}"</p>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold text-[#4A7C59] uppercase tracking-wide">Reality</span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.reality}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* How to Get the Lowest Rate */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Money-Saving Tips</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                7 Ways to Get the Most Affordable Life Insurance in Indianapolis
              </h2>
              <div className="flex flex-col gap-4">
                {[
                  {
                    num: '01',
                    title: 'Buy now, not later',
                    desc: 'Life insurance rates increase with age. A 30-year-old pays $30/month for $500K of 20-year term. The same policy at 40 costs $58/month — nearly double. Every year you wait costs you money.',
                    icon: 'ri-time-line',
                  },
                  {
                    num: '02',
                    title: 'Compare multiple carriers',
                    desc: 'Rates for the same coverage can vary 30–50% between carriers. A 40-year-old seeking $500K of 20-year term might pay $58/month with Banner Life or $68/month with another carrier. Always compare.',
                    icon: 'ri-search-line',
                  },
                  {
                    num: '03',
                    title: 'Choose term over whole life',
                    desc: 'Term life costs 10–15x less than whole life for the same death benefit. A 35-year-old pays $38/month for $500K of 20-year term vs. $660/month for $500K of whole life. For most Indianapolis families, term is the right choice.',
                    icon: 'ri-calendar-check-line',
                  },
                  {
                    num: '04',
                    title: 'Quit tobacco (or wait 12 months)',
                    desc: 'Tobacco users pay 2–3x more than non-tobacco users. If you\'ve quit for 12+ months, you may qualify for non-tobacco rates. A 40-year-old non-tobacco user pays $58/month for $500K of 20-year term; a tobacco user pays $140–$180/month.',
                    icon: 'ri-forbid-line',
                  },
                  {
                    num: '05',
                    title: 'Improve your health class',
                    desc: 'Losing weight, controlling blood pressure, or improving cholesterol before applying can move you from Standard to Preferred — saving $20–$40/month on a $500K policy. Ask us about a "trial application" to test your health class before committing.',
                    icon: 'ri-heart-pulse-line',
                  },
                  {
                    num: '06',
                    title: 'Pay annually instead of monthly',
                    desc: 'Most carriers offer a 3–5% discount for annual premium payments. On a $50/month policy, that\'s $18–$30/year in savings — not huge, but free money.',
                    icon: 'ri-calendar-2-line',
                  },
                  {
                    num: '07',
                    title: 'Work with an independent agent',
                    desc: 'Independent agents like Hoosier Life Insurance compare 20+ carriers simultaneously. Captive agents (State Farm, Allstate) can only quote one company. The difference can be $30–$50/month for the same coverage.',
                    icon: 'ri-user-star-line',
                  },
                ].map(tip => (
                  <div key={tip.num} className="bg-white rounded-xl p-6 border border-gray-100 flex items-start gap-5">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#D97642]/10">
                        <i className={`${tip.icon} text-xl text-[#D97642]`}></i>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-xs font-extrabold text-[#D97642] tracking-widest">{tip.num}</span>
                        <h3 className="text-base font-bold text-[#1A3A52]">{tip.title}</h3>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Families Who Found Affordable Coverage
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    {/* Budget badge */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.budget}</span>
                      <span className="bg-[#1A3A52]/10 text-[#1A3A52] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Affordable Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-ali-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN — Sticky Sidebar */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">

            {/* Quote CTA */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-money-dollar-circle-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Affordable Quote</h3>
                    <p className="text-xs text-white/70">20+ carriers · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ From $15/month</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ 20+ carriers compared</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Zero pressure</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a
                    href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link
                    to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm"
                  >
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · Results in 15 minutes</p>
              </div>
            </div>

            {/* Quick Rate Snapshot */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-money-dollar-circle-line text-[#D97642]"></i>
                Affordable Rates — Indianapolis, IN
              </h3>
              <div className="flex flex-col gap-3">
                {quickLookRows.map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-sm font-semibold text-[#1A3A52]">Age {row.age} · {row.coverage}</p>
                      <p className="text-xs text-gray-400">{row.term} · {row.carrier}</p>
                    </div>
                    <span className="text-base font-extrabold text-[#D97642] whitespace-nowrap ml-3">{row.monthly}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Preferred Plus · Non-tobacco · Estimates only.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Quote Call</h3>
                    <p className="text-xs text-white/70">We find your most affordable option</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Quote Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: "Anything you'd like us to know? (Optional)",
                    submitButton: 'Confirm My Free Quote Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare Quotes — 20+ Carriers', icon: 'ri-search-line' },
                  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
                  { to: '/final-expense-insurance-indianapolis', label: 'Final Expense Insurance Indianapolis', icon: 'ri-heart-pulse-line' },
                  { to: '/burial-insurance-indianapolis', label: 'Burial Insurance Indianapolis', icon: 'ri-shield-check-line' },
                  { to: '/instant-quote', label: 'Instant Online Quote Tool', icon: 'ri-file-list-3-line' },
                  { to: '/no-medical-exam', label: 'No Medical Exam Life Insurance', icon: 'ri-hospital-line' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Meet Your Agent */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20male%20insurance%20agent%20in%20his%20late%2030s%20wearing%20a%20warm%20charcoal%20blazer%20and%20light%20blue%20shirt%20friendly%20approachable%20smile%20sitting%20at%20a%20clean%20modern%20desk%20with%20natural%20window%20light%20Indianapolis%20Indiana%20office%20setting%20trustworthy%20financial%20advisor%20helping%20clients%20find%20affordable%20life%20insurance%20budget-friendly%20coverage%20Marion%20County%20Indiana%20professional%20portrait%20photography&width=600&height=700&seq=agent-bwright-ali-indy-2025a&orientation=portrait"
                alt="Benjamin Wright — Affordable Life Insurance Agent in Indianapolis, Indiana"
                title="Benjamin Wright — Affordable Life Insurance Specialist in Indianapolis"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl w-full h-[480px] object-cover object-top"
              />
              <div className="absolute bottom-6 right-6 bg-white rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#D97642] rounded-full flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Starting at</div>
                    <div className="text-base font-bold text-[#1A3A52]">$15/month</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-user-heart-line"></i>
                Your Local Indianapolis Budget Specialist
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-6">Meet Benjamin Wright</h2>
              <p className="text-base text-gray-600 mb-5 leading-relaxed">
                I've spent 12+ years helping Indianapolis and Marion County families find affordable life insurance that actually fits their budget. My job isn't to sell you the most expensive policy — it's to find the most coverage for the least money.
              </p>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                As an independent agent, I compare 20+ A-rated carriers simultaneously. That means you get the lowest available rate — not the rate that pays me the highest commission. Most Indianapolis families are surprised by how affordable real coverage actually is.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="px-7 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer"
                >
                  Find My Affordable Rate
                </button>
                <a
                  href="tel:+13176482983"
                  className="px-7 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg font-semibold hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap cursor-pointer"
                >
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Links */}
      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3">Explore All Coverage Options</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">Find the most affordable life insurance for your specific situation</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { to: '/life-insurance-quotes-indianapolis', icon: 'ri-search-line', title: 'Compare Quotes Indianapolis', desc: 'Interactive 6-carrier rate comparison tool — see all options ranked lowest to highest' },
              { to: '/term-life-insurance-indianapolis', icon: 'ri-calendar-check-line', title: 'Term Life Insurance Indianapolis', desc: 'The most affordable option — $500K of coverage from $30/month for Indianapolis families' },
              { to: '/final-expense-insurance-indianapolis', icon: 'ri-heart-pulse-line', title: 'Final Expense Insurance Indianapolis', desc: 'Affordable senior coverage from $30/month — no medical exam required' },
              { to: '/burial-insurance-indianapolis', icon: 'ri-shield-check-line', title: 'Burial Insurance Indianapolis', desc: 'Cover funeral costs from $30/month — protect your family from a $9K–$15K expense' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-xl p-6 transition-all group cursor-pointer"
              >
                <i className={`${link.icon} text-3xl text-[#D97642] group-hover:text-white mb-3 block`}></i>
                <h3 className="font-bold text-base mb-2 text-[#1A3A52] group-hover:text-white">{link.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-gray-200">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Affordable life insurance in Indianapolis starts at $15/month.
          </h2>
          <p className="text-base text-white/75 mb-7">
            Use our Budget Calculator to see exactly what your budget buys — then call for your exact personalized rate. 20+ A-rated carriers compared. No obligation, no pressure.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#budget-calculator"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calculator-line text-lg"></i>
              Use Budget Calculator
            </a>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Affordable Life Insurance Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Affordable Life Insurance" quoteHref="/instant-quote" />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Find Your Most Affordable Indianapolis Life Insurance Rate</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="Book Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
